/**
 * The machine, as a thing in a state — the derivations both clients make before
 * a fleet card, a marker or a lifecycle rail is drawn.
 *
 * These live here for the reason `discovery.ts` does: they are readings of the
 * contract, not a screen's opinion. A machine that reads "on hire" on the web
 * and "available" on the phone is worse than one that reads neither, and two
 * copies of a rule are how that happens. It has already happened once in this
 * repository — `machineMotif` existed twice, the two regexes drifted by one
 * word boundary, and the web copy quietly drew a crane for every forklift.
 *
 * Nothing here invents a state. Every value returned is derived from a field
 * the API sent, and the two places where the contract withholds a field are
 * handled by refusing to answer rather than by guessing:
 *
 *   · §9.5 gives a blocking interval's `reason` to the OWNER only. A non-owner
 *     learns THAT a machine is unavailable and never why, so the derivation
 *     returns `unavailable` for them and never a reason-derived status.
 *   · No endpoint returns a location (§4 of PRODUCT_UX_ARCHITECTURE.md), so
 *     nothing here produces one, and `mapCapability` reports what the backend
 *     itself said about geospatial support rather than what the client assumes.
 */
const LABEL = {
    available: 'Available',
    unavailable: 'Unavailable',
    onHire: 'On hire',
    inTransport: 'In transport',
    handover: 'At handover',
    maintenance: 'Out of service',
    unpublished: 'Not published',
    paused: 'Paused',
    attention: 'Needs attention',
    retired: 'Retired',
};
const TONE = {
    available: 'success',
    // Neutral, not danger: a machine being busy is the business working.
    unavailable: 'neutral',
    onHire: 'info',
    inTransport: 'info',
    handover: 'info',
    // Warning, not danger. BR-23.4's principle applies to the whole product:
    // a recorded, deliberate, legitimate state must never read as a fault.
    maintenance: 'warning',
    unpublished: 'neutral',
    paused: 'neutral',
    attention: 'warning',
    retired: 'neutral',
};
/**
 * Which reason means what.
 *
 * `soft_hold` is deliberately absent, and its absence is a product decision
 * rather than an oversight: a soft hold is a stranger's unfinished checkout,
 * and reporting a machine as busy because somebody else is mid-decision would
 * misstate its availability to its own owner. It falls through to `available`,
 * which is what it is until the hold converts.
 */
const REASON_STATUS = {
    confirmed: 'onHire',
    extension: 'onHire',
    buffer_out: 'inTransport',
    buffer_in: 'inTransport',
    maintenance: 'maintenance',
    blackout: 'paused',
};
/** Publication states that answer on their own, before any interval is read. */
const PUBLICATION_STATUS = {
    retired: 'retired',
    suspended: 'attention',
    draft: 'unpublished',
    pending_review: 'unpublished',
    paused: 'paused',
};
function covering(intervals, at) {
    for (const interval of intervals) {
        const start = Date.parse(interval.start);
        const end = Date.parse(interval.end);
        // A half-open range, matching `tstzrange(..., '[)')` in the database.
        if (Number.isFinite(start) && Number.isFinite(end) && start <= at && end > at)
            return interval;
    }
    return null;
}
/**
 * What this machine is doing, right now, in one word and one supporting fact.
 *
 * The order of the branches is the whole of the rule, and it is this way round
 * because a machine that is not published cannot be on hire: its listing state
 * is a harder fact than anything an interval could say about it.
 */
export function machineStatus(input) {
    const at = (input.at ?? new Date()).getTime();
    const fromPublication = PUBLICATION_STATUS[input.publication_state];
    if (fromPublication !== undefined) {
        return reading(fromPublication, null, false);
    }
    // Anything that is not published and not named above is not a state this
    // client knows. Saying so is better than mapping it onto a state it is not.
    if (input.publication_state !== 'published') {
        return reading('attention', null, true);
    }
    // A booking at handover is the most specific thing that can be true of a
    // published machine, and no interval reports it.
    if (input.bookingState === 'handover_pending')
        return reading('handover', null, false);
    const interval = covering(input.blocked ?? [], at);
    if (interval === null)
        return reading('available', null, false);
    // §9.5 — the reason was withheld, so the reading stops at what the viewer is
    // entitled to know. It does NOT fall back to a guess at the likeliest reason.
    if (interval.reason === null)
        return reading('unavailable', interval.end, false);
    const status = REASON_STATUS[interval.reason];
    if (status === undefined)
        return reading('available', null, false);
    return reading(status, interval.end, true);
}
function reading(status, until, ownerOnly) {
    return { status, label: LABEL[status], until, tone: TONE[status], ownerOnly };
}
const PUBLICATION_READING = {
    published: { label: 'Published', tone: 'success' },
    pending_review: { label: 'In review', tone: 'info' },
    draft: { label: 'Draft', tone: 'neutral' },
    paused: { label: 'Paused', tone: 'neutral' },
    suspended: { label: 'Suspended', tone: 'warning' },
    retired: { label: 'Retired', tone: 'neutral' },
};
export function publicationReading(state) {
    // An unknown state is printed as the backend sent it rather than mapped onto
    // a word it did not choose. A wrong label on a fleet list is worse than an
    // unfamiliar one.
    return PUBLICATION_READING[state] ?? { label: humanisePublication(state), tone: 'neutral' };
}
function humanisePublication(state) {
    const words = state.replace(/_/g, ' ');
    return words.charAt(0).toUpperCase() + words.slice(1);
}
/** Every status, for a legend or a filter. Ordered as an owner reads a fleet. */
export const MACHINE_STATUS_ORDER = [
    'available',
    'onHire',
    'inTransport',
    'handover',
    'maintenance',
    'paused',
    'unpublished',
    'attention',
    'unavailable',
    'retired',
];
export function machineStatusLabel(status) {
    return LABEL[status];
}
export function machineStatusTone(status) {
    return TONE[status];
}
const BEATS = [
    'request',
    'offer',
    'booked',
    'transport',
    'handover',
    'running',
    'return',
    'bill',
];
const BEAT_LABEL = {
    request: 'Request',
    offer: 'Offer',
    booked: 'Booked',
    transport: 'Transport',
    handover: 'Handover',
    running: 'Running',
    return: 'Return',
    /*
     * "Billing", not "Bill".
     *
     * The BIL state machine is labelled "Bill" in `MachineStateRow`, and the two
     * sit in the same section on the booking hub — the rail above, the machines
     * beneath. One word for a BEAT of the journey and for a STATE MACHINE, eight
     * inches apart, is how a reader comes to believe they are the same thing and
     * then cannot explain why one says "done" and the other says "provisional".
     */
    bill: 'Billing',
};
/** Where each BKG state sits on the rail. Every enum member is accounted for. */
const STATE_BEAT = {
    pending_payment: 'booked',
    confirmed: 'transport',
    dispatch_scheduled: 'transport',
    in_transit: 'transport',
    handover_pending: 'handover',
    active: 'running',
    return_pending: 'return',
    returned: 'bill',
    billing_review: 'bill',
    settled: 'bill',
    closed: 'bill',
};
/**
 * The states that end a rental, and where each one ended it.
 *
 * A terminal state is not "the last beat"; it is a beat that stopped. Showing
 * the remaining beats as still upcoming after a cancellation would promise a
 * handover that is never coming.
 */
const TERMINAL = {
    cancelled: { beat: 'booked', note: 'Cancelled' },
    expired: { beat: 'booked', note: 'Expired before payment' },
    abandoned: { beat: 'booked', note: 'Abandoned' },
    rejected_at_handover: { beat: 'handover', note: 'Rejected at handover' },
    terminated_early: { beat: 'running', note: 'Ended early' },
};
/** The two states where the rail is complete rather than merely at its end. */
const COMPLETE = new Set(['settled', 'closed']);
/** Which beat each flag is about. */
const FLAG_BEAT = {
    payment_overdue: { beat: 'booked', note: 'Payment overdue' },
    dispatch_overdue: { beat: 'transport', note: 'Dispatch overdue' },
    evidence_incomplete: { beat: 'handover', note: 'Evidence incomplete' },
    disputed: { beat: 'bill', note: 'Contested' },
};
/**
 * The rail, as eight steps.
 *
 * `request` and `offer` are always `done`: a booking exists only because an
 * offer was accepted, so the two beats before it are history by definition.
 * They are still drawn, because the rail's job is to show the whole journey and
 * a customer who joins at `booked` should see what they already passed.
 */
export function rentalLifecycle(input) {
    const terminal = TERMINAL[input.state];
    const flags = input.flags ?? {};
    // Which beats a flag has something to say about, and what it says.
    const noteFor = new Map();
    for (const [flag, target] of Object.entries(FLAG_BEAT)) {
        if (flags[flag] === true)
            noteFor.set(target.beat, target.note);
    }
    if (terminal !== undefined) {
        const stopIndex = BEATS.indexOf(terminal.beat);
        return BEATS.map((beat, index) => ({
            beat,
            label: BEAT_LABEL[beat],
            status: index < stopIndex ? 'done' : index === stopIndex ? 'stopped' : 'upcoming',
            note: index === stopIndex ? terminal.note : null,
        }));
    }
    const currentBeat = STATE_BEAT[input.state];
    // An unknown state is not forced onto the rail. Every beat reads `upcoming`
    // and the caller's machine row remains the authority — which is the correct
    // outcome, because the rail is a summary and the machines are the record.
    if (currentBeat === undefined) {
        return BEATS.map((beat) => ({
            beat,
            label: BEAT_LABEL[beat],
            status: 'upcoming',
            note: null,
        }));
    }
    const complete = COMPLETE.has(input.state);
    const currentIndex = BEATS.indexOf(currentBeat);
    return BEATS.map((beat, index) => {
        const note = noteFor.get(beat) ?? null;
        if (complete)
            return { beat, label: BEAT_LABEL[beat], status: 'done', note: null };
        if (index < currentIndex) {
            return { beat, label: BEAT_LABEL[beat], status: 'done', note: null };
        }
        if (index > currentIndex) {
            return { beat, label: BEAT_LABEL[beat], status: 'upcoming', note: null };
        }
        // The current beat, and only the current beat, can be blocked — by a flag
        // the API set, naming that beat.
        return {
            beat,
            label: BEAT_LABEL[beat],
            status: (note === null ? 'current' : 'blocked'),
            note,
        };
    });
}
/**
 * Everything the API flagged, whichever beat it belongs to.
 *
 * `rentalLifecycle` can only mark the CURRENT beat, so a flag about a beat the
 * rental has already passed — a contested bill on a closed booking — would be
 * silently dropped by the rail alone. This returns all of them, so a screen can
 * state them plainly rather than relying on a dot somewhere along a line.
 */
export function lifecycleAttention(input) {
    const flags = input.flags ?? {};
    return Object.entries(FLAG_BEAT)
        .filter(([flag]) => flags[flag] === true)
        .map(([, target]) => ({ beat: target.beat, note: target.note }));
}
/** `booking.transport_leg.state`, in the order M026's enum declares them. */
export const TRANSPORT_PHASES = [
    { key: 'planned', label: 'Planned' },
    { key: 'scheduled', label: 'Scheduled' },
    { key: 'dispatched', label: 'Dispatched' },
    { key: 'in_transit', label: 'In transit' },
    { key: 'arrived', label: 'Arrived' },
    { key: 'completed', label: 'Completed' },
];
/**
 * Media, from the camera to something the platform will serve.
 *
 * `pending_scan → clean` is `public.media_scan_status`, and the two steps before
 * it are the upload seam P-C095/P-C096 defines. A capture sitting in
 * `pending_scan` is the state an operations screen most needs to show, because
 * it is the one where nothing appears to be happening and the user is most
 * likely to press the button again.
 */
export const EVIDENCE_PHASES = [
    { key: 'captured', label: 'Captured' },
    { key: 'uploading', label: 'Uploading' },
    { key: 'pending_scan', label: 'Checking' },
    { key: 'clean', label: 'Ready' },
];
export function phaseSequence(sequence, state) {
    const index = sequence.findIndex((phase) => phase.key === state);
    if (index === -1) {
        return {
            phases: sequence.map((phase) => ({ ...phase, status: 'upcoming' })),
            offSequence: true,
        };
    }
    return {
        phases: sequence.map((phase, position) => ({
            ...phase,
            status: position < index ? 'done' : position === index ? 'current' : 'upcoming',
        })),
        offSequence: false,
    };
}
/**
 * Whether this product can show a map, according to the backend.
 *
 * This is deliberately NOT a constant in the client. `POST /v1/equipment:search`
 * returns `search_meta.geospatial` on every response, and refuses `site` and
 * `radius_km` outright with `409 config.pending_decision {decision:'BLK-4'}` —
 * so the API states its own geospatial position and the UI repeats it.
 *
 * The consequence is the one that matters: the day an endpoint starts returning
 * a location, the map turns on without a line of this UI being redesigned. A
 * hard-coded `false` would have to be found and removed by hand, and the map
 * layer it disabled would have rotted in the meantime.
 *
 * The database is already ready for this — M033 installed PostGIS, `geo.location`
 * holds an exact point, and `geo.public_cell` is a generated 1 km display grid
 * that cannot be used for distance (INV-19). What is missing is disclosure on an
 * endpoint, not capability in the schema.
 */
export function mapCapability(meta) {
    if (meta === undefined || meta === null || typeof meta.geospatial !== 'string') {
        return {
            geo: 'unknown',
            reason: null,
            detail: 'Machine locations have not been read yet.',
        };
    }
    if (meta.geospatial === 'unsupported') {
        return {
            geo: 'unsupported',
            reason: meta.geospatial_reason ?? null,
            detail: 'This platform does not publish machine locations yet, so there is no map to show.',
        };
    }
    return {
        geo: 'available',
        reason: meta.geospatial_reason ?? null,
        detail: 'Locations available.',
    };
}
const MOTIFS = [
    // Machine TYPES and the catalogue's own CATEGORY words. A manufacturer's name
    // says nothing about what a machine is — the same maker builds excavators,
    // dozers and dumpers — so matching on one would be a guess wearing a drawing.
    // A category name, by contrast, is the catalogue's own statement about what
    // the machines under it are.
    //
    // ORDER IS LOAD-BEARING. The compound words come before the words they
    // contain: `forklift` is matched before `lift`, `telehandler` before
    // `handler`. Both boundaries are anchored on every bare word for the same
    // reason — `\blift\b`, never `lift\b`, which "Forklift" satisfies.
    [/forklift|telehandl|stacker|pallet|material.?handl|warehous/i, 'forklift'],
    [
        /excavat|digger|backhoe|jcb|dozer|bulldoz|loader|grader|trench|earthmov|earth.?work|compact|roller|paver/i,
        'excavator',
    ],
    [/crane|boom|hoist|derrick|\blift(?:ing)?\b|aerial|access platform/i, 'crane'],
    [/truck|tipper|dumper|haul|lorr|trailer|mixer|tanker|transport|concrete|batch/i, 'truck'],
    [/tractor|harvest|plough|plow|thresher|agricultur|farm/i, 'tractor'],
    [/container|cabin|skip|silo|tank|storage|site.?office|generator|power/i, 'container'],
];
export function machineMotif(title) {
    if (typeof title !== 'string')
        return 'machine';
    for (const [pattern, motif] of MOTIFS) {
        if (pattern.test(title))
            return motif;
    }
    return 'machine';
}
//# sourceMappingURL=machine.js.map